import React, { Fragment, useEffect, useState } from 'react';
import { Confirm, useUnselectAll, useNotify, useRefresh } from 'react-admin';
import Button from '@material-ui/core/Button';
import DialogContentText from '@material-ui/core/DialogContentText';
import GeoStatusAlert from 'components/GeoStatusAlert';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { LocationProvider } from 'providers/locationProvider';
import { CAN_APPROVE_PHASE } from 'providers/permissions';
import { dataProvider } from 'providers/dataProvider';

interface PostBulkActionButtonsProps {
  selectedIds?: string[];
  resource?: string;
}

export const PostBulkActionButtons: React.FC<PostBulkActionButtonsProps> =
  props => {
    const [open, setOpen] = useState(false);
    const [openRequestConfirm, setOpenRequestConfirm] = useState(false);
    const [isApproving, setIsApproving] = useState(false);
    const { hasPermission } = PermissionsProvider.useContainer();
    const {
      coords: geoCoords,
      accuracy: geoAccuracy,
      loading: geoLoading,
      error: geoError,
      requestLocation,
    } = LocationProvider.useContainer();
    const unselectAll = useUnselectAll();
    const notify = useNotify();
    const refresh = useRefresh();

    useEffect(() => {
      if (open) {
        requestLocation();
      }
    }, [open, requestLocation]);

    const handleConfirm = async () => {
      try {
        setIsApproving(true);
        const geoData = geoCoords && {
          approvedGeo: {
            type: 'Point',
            coordinates: [geoCoords[0], geoCoords[1]],
          },
          approvedGeoAccuracy: geoAccuracy,
        };
        await dataProvider.create('job-phase-work-approval-batch/bulk', {
          data: {
            bulk: (props.selectedIds || []).map(item => ({
              id: item,
              ...geoData,
            })),
          },
        });
        notify('Approved with success!');
        refresh();
        unselectAll('job-master-sheet');
        unselectAll('job-master-sheet-v2');
        unselectAll('job-warranty-sheet');
        unselectAll('job-warranty-sheet-v2');
      } catch (error: any) {
        const errors = error?.response?.data?.message;
        if (Array.isArray(errors)) {
          errors.forEach((item: any) => {
            notify('Lot:' + item.job.lot + ' ' + item.errorMessage, 'warning');
          });
        } else {
          notify('An unexpected error occurred.', 'warning');
        }
      } finally {
        setIsApproving(false);
        setOpen(false);
      }
    };

    const handleConfirmRequestResource = async () => {
      try {
        await dataProvider.create('job-phase-request-resource-batch/bulk', {
          data: {
            bulk: (props.selectedIds || []).map(item => ({ id: item })),
          },
        });
        notify('Resource request has been submitted.');
        refresh();
        unselectAll('job-master-sheet');
        unselectAll('job-master-sheet-v2');
        unselectAll('job-warranty-sheet');
        unselectAll('job-warranty-sheet-v2');
      } catch (error: any) {
        const errors = error?.response?.data?.message;
        if (Array.isArray(errors)) {
          errors.forEach((item: any) => {
            notify('Lot:' + item.job.lot + ' ' + item.errorMessage, 'warning');
          });
        } else {
          notify('An unexpected error occurred.', 'warning');
        }
      } finally {
        setOpenRequestConfirm(false);
      }
    };

    return (
      <Fragment>
        {hasPermission(CAN_APPROVE_PHASE) && (
          <Button onClick={() => setOpen(true)}>
            <label>Approve</label>
          </Button>
        )}
        {hasPermission(CAN_APPROVE_PHASE) && (
          <Button onClick={() => setOpenRequestConfirm(true)}>
            <label>Request Resource</label>
          </Button>
        )}
        <Confirm
          isOpen={openRequestConfirm}
          title="Request Resources"
          content="Are you sure you want to request resource for these phases? Attention: If any of the selected jobs are NOT requested correctly, NO action will be taken on any job."
          onConfirm={handleConfirmRequestResource}
          onClose={() => setOpenRequestConfirm(false)}
        />
        <Confirm
          isOpen={open}
          loading={isApproving}
          title="Approve phases"
          content={
            <>
              <DialogContentText>
                Are you sure you want to approve these phases? Attention: If
                any of the selected jobs are NOT able to be approved, NO action
                will be taken on any job.
              </DialogContentText>
              <GeoStatusAlert
                loading={geoLoading}
                error={geoError}
                coords={geoCoords}
                action="approving"
              />
            </>
          }
          onConfirm={handleConfirm}
          onClose={() => setOpen(false)}
        />
      </Fragment>
    );
  };
